import { gql, useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import {
  EqlField,
  EqlFieldValue,
  Query,
  ResolveEqlReferencesInput,
} from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';

const FETCH_EQL_REFERENCES = gql`
  query resolveEQLReferences($tenantID: ID!, $eql: String!) {
    resolveEQLReferences(request: { tenantID: $tenantID, eql: $eql }) {
      filters {
        field {
          field
          displayName
          iconURL
          valueType
          objectType
          isMultiValue
          detail {
            metadata {
              source
              references {
                key
                displayName
                iconURL
                source
              }
            }
          }
        }
        values {
          value
          displayName
          iconURL
        }
      }
    }
  }
`;

export type EqlFieldByID = Record<
  string,
  | {
      field?: EqlField;
      eqlFieldValueByID?: Record<string, EqlFieldValue | undefined>;
    }
  | undefined
>;

interface useFetchEQLReferencesProps {
  whereClause: WhereClause;
  onCompleted?: (data: EqlFieldByID) => void;
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
}

const getFieldIDToValueMap = (values: EqlFieldValue[]) => {
  const fieldIDToValueMap: Record<string, EqlFieldValue> = {};

  values.forEach((value) => {
    fieldIDToValueMap[value.value] = value;
  });

  return fieldIDToValueMap;
};

export const useFetchEQLReferences = ({
  whereClause,
  onCompleted,
  fetchPolicy = 'cache-first',
  skip = false,
}: useFetchEQLReferencesProps) => {
  const [eqlFieldByID, setEqlFieldByID] = useState<EqlFieldByID>({});

  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { eql = '' } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause,
      expressionType: ExpressionType.Records,
    },
  });

  const shouldSkipCall = skip || !eql;

  useEffect(() => {
    if (shouldSkipCall) {
      setEqlFieldByID({});
    }
  }, [shouldSkipCall]);

  const { loading } = useQuery<Query, ResolveEqlReferencesInput>(
    FETCH_EQL_REFERENCES,
    {
      variables: {
        tenantID,
        eql,
      },
      onCompleted(data) {
        const filterValueMap: EqlFieldByID = {};

        data?.resolveEQLReferences?.filters.forEach((filter) => {
          filterValueMap[filter.field.field] = {
            field: filter.field,
            eqlFieldValueByID: {
              ...getFieldIDToValueMap(filter.values),
            },
          };
        });

        setEqlFieldByID(filterValueMap);
        onCompleted?.(filterValueMap);
      },
      fetchPolicy,
      nextFetchPolicy: 'cache-first',
      skip: shouldSkipCall,
    },
  );

  return {
    eqlFieldByID,
    isLoading: loading,
  } as const;
};
